// insertAtomBlockNode.js — shared positioning logic for inserting a single atom
// block node (map, etc.) either at an explicit range (atomic replace, e.g. a
// slash-command's trigger block) or at the current selection (today's toolbar
// behavior: replace an empty textblock in place, otherwise insert-after with a
// trailing empty paragraph).

/**
 * Inserts (or atomically replaces) an atom block node at a specific location.
 *
 * @param {EditorView} view
 * @param {Node} node - the ProseMirror node to insert (already created via type.create(attrs))
 * @param {Object} [opts]
 * @param {{from: number, to: number}} [opts.range] - explicit range to atomically replace with the node
 *   (e.g. a slash-command's trigger block). When omitted, falls back to inferring position from the
 *   current selection: if selection is inside an empty textblock, replace that block; otherwise insert
 *   after the selection and append a trailing empty paragraph (today's toolbar behavior, unchanged).
 * @param {Array<[PluginKey, any]>} [opts.meta] - extra [pluginKey, metaValue] pairs to set on the transaction
 *   (e.g. closing the slash menu) so the whole operation stays one atomic transaction/dispatch.
 * @returns {boolean}
 */
export function insertAtomBlockNode(view, node, opts = {}) {
    if (!view) return false;
    const {dispatch} = view;
    if (!dispatch) return false;

    const {state} = view;
    const {schema, selection} = state;
    const {range, meta} = opts;

    let tr;
    if (range) {
        tr = state.tr.replaceWith(range.from, range.to, node);
    } else {
        const {$from, $to} = selection;
        const isEmptyBlock =
            $from.parent.isTextblock &&
            $from.parent.childCount === 0 &&
            $from.parent.textContent === '';

        if (isEmptyBlock) {
            const start = $from.before($from.depth);
            const end = $from.after($from.depth);
            tr = state.tr.replaceWith(start, end, node);
        } else {
            const insertPos = $to.after($to.depth);
            const paragraph = schema.nodes.paragraph.createAndFill();
            tr = state.tr.insert(insertPos, [node, paragraph].filter(Boolean));
        }
    }

    if (meta) {
        meta.forEach(([pluginKey, metaValue]) => tr.setMeta(pluginKey, metaValue));
    }

    tr.scrollIntoView();
    dispatch(tr);

    return true;
}
